import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import type { RootState } from "../../../store/store";
import {
  AejPendency,
  ChainReport,
  fetchOfficialFileApi,
  getRepStatusApi,
  OfficialFileError,
  RepStatus,
  startRepApi,
  verifyChainsApi,
} from "../api/timekeepingApi";
import { DP_TIMESHEET_PAGE } from "../routes";
import { downloadBlob, formatCnpj } from "../utils/format";

type Props = { year: number; month: number; onClose: () => void };

const pad = (n: number) => String(n).padStart(2, "0");
const monthStart = (y: number, m: number) => `${y}-${pad(m)}-01`;
const monthEnd = (y: number, m: number) => `${y}-${pad(m)}-${pad(new Date(Date.UTC(y, m, 0)).getUTCDate())}`;
const brDate = (iso: string) => iso.split("-").reverse().join("/");

/**
 * Arquivos oficiais da Portaria 671 (ADR 0042): o registro de ponto de cada
 * empregador (ARP), o AFD e o AEJ do período e a conferência de integridade. Só
 * ADMIN e DP chegam aqui (a tela do ponto já é deles).
 */
export default function OfficialFilesModal({ year, month, onClose }: Props) {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.accessToken) as string;
  const [status, setStatus] = useState<RepStatus | null>(null);
  const [employerId, setEmployerId] = useState("");
  const [from, setFrom] = useState(monthStart(year, month));
  const [to, setTo] = useState(monthEnd(year, month));
  const [busy, setBusy] = useState<string | null>(null);
  const [pendencies, setPendencies] = useState<AejPendency[]>([]);
  const [chain, setChain] = useState<ChainReport | null>(null);
  const [confirmStart, setConfirmStart] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getRepStatusApi(token);
      setStatus(data);
      setEmployerId((current) => current || data.employers[0]?.employerId || "");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar o registro de ponto.");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const employer = status?.employers.find((e) => e.employerId === employerId) ?? null;

  const generate = async (kind: "afd" | "aej") => {
    if (!employerId) return;
    if (to < from) {
      toast.error("O fim do período deve ser depois do início.");
      return;
    }
    setBusy(kind);
    setPendencies([]);
    try {
      const { blob, fileName } = await fetchOfficialFileApi(token, kind, { employerId, from, to });
      downloadBlob(blob, fileName);
    } catch (error: any) {
      if (error instanceof OfficialFileError && error.pendencies.length) setPendencies(error.pendencies);
      toast.error(error?.message || "Erro ao gerar o arquivo.");
    } finally {
      setBusy(null);
    }
  };

  const start = async () => {
    if (!employerId) return;
    setBusy("start");
    try {
      const result = await startRepApi(token, employerId);
      toast.success(`Registro de ponto iniciado: ${result.included} colaborador(es) incluído(s).`);
      setConfirmStart(false);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao iniciar o registro de ponto.");
    } finally {
      setBusy(null);
    }
  };

  const verify = async () => {
    setBusy("verify");
    try {
      setChain(await verifyChainsApi(token));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao conferir as marcações.");
    } finally {
      setBusy(null);
    }
  };

  const openPendency = (p: AejPendency) => {
    if (!p.contractId || !p.day) return;
    const [y, m] = p.day.split("-").map(Number);
    navigate(DP_TIMESHEET_PAGE(p.contractId, y, m));
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal dpModal--wide">
        <header className="dpModal__header">
          <h2 className="dpModal__title">Arquivos oficiais (Portaria 671)</h2>
          <button className="dpModal__close" onClick={onClose} aria-label="Fechar">
            &times;
          </button>
        </header>

        <div className="dpModal__body">
          {status === null ? (
            <p className="dpMuted">Carregando...</p>
          ) : (
            <>
              {status.warnings.length ? (
                <div className="dpNotice dpNotice--warning">
                  <ul className="dpRuleList" style={{ margin: 0 }}>
                    {status.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {status.employers.length > 1 ? (
                <div className="dpFormGroup">
                  <label>Empregador</label>
                  <select value={employerId} onChange={(e) => setEmployerId(e.target.value)}>
                    {status.employers.map((e) => (
                      <option key={e.employerId} value={e.employerId}>
                        {e.legalName}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {employer ? (
                <section className="tkOfficial__section">
                  <h3 className="tkOfficial__title">Registro de ponto</h3>
                  <p className="dpMuted" style={{ margin: "0 0 8px" }}>
                    {employer.legalName} · CNPJ {formatCnpj(employer.cnpj)}
                  </p>
                  {employer.started ? (
                    <p style={{ margin: 0 }}>
                      Iniciado em {new Date(employer.startedAt!).toLocaleDateString("pt-BR")} · último NSR {employer.lastNsr}
                    </p>
                  ) : (
                    <>
                      <p className="dpNotice dpNotice--warning">
                        O registro ainda não foi iniciado: o quiosque não aceita marcação deste empregador até lá. Iniciar grava a empresa e inclui os
                        colaboradores com contrato vigente; quem for admitido depois entra sozinho.
                      </p>
                      <button type="button" className="dpBtn dpBtn--primary" onClick={() => setConfirmStart(true)} disabled={busy !== null}>
                        Iniciar registro de ponto
                      </button>
                    </>
                  )}
                </section>
              ) : (
                <p className="dpNotice dpNotice--warning">Nenhum empregador ativo cadastrado.</p>
              )}

              <section className="tkOfficial__section">
                <h3 className="tkOfficial__title">AFD e AEJ</h3>
                <div className="tkOfficial__period">
                  <div className="dpFormGroup" style={{ margin: 0 }}>
                    <label htmlFor="official-from">De</label>
                    <input id="official-from" type="date" className="dpInput" value={from} onChange={(e) => setFrom(e.target.value)} />
                  </div>
                  <div className="dpFormGroup" style={{ margin: 0 }}>
                    <label htmlFor="official-to">Até</label>
                    <input id="official-to" type="date" className="dpInput" value={to} onChange={(e) => setTo(e.target.value)} />
                  </div>
                </div>
                <div className="tkOfficial__actions">
                  <button type="button" className="dpBtn dpBtn--primary" onClick={() => generate("afd")} disabled={!employer || busy !== null}>
                    {busy === "afd" ? "Gerando..." : "Gerar AFD"}
                  </button>
                  <button type="button" className="dpBtn dpBtn--primary" onClick={() => generate("aej")} disabled={!employer || busy !== null}>
                    {busy === "aej" ? "Gerando..." : "Gerar AEJ"}
                  </button>
                </div>
                <p className="dpMuted" style={{ margin: "8px 0 0" }}>
                  AFD: as marcações originais, como o fiscal pede. AEJ: a jornada tratada pelo DP (inclusões e desconsiderações com o motivo, folgas e
                  faltas); não sai com pendência.
                </p>

                {pendencies.length ? (
                  <div className="dpNotice dpNotice--warning" style={{ marginTop: 12 }}>
                    <strong>Corrija antes de gerar o AEJ:</strong>
                    <ul className="dpRuleList" style={{ margin: "6px 0 0" }}>
                      {pendencies.map((p, i) => (
                        <li key={`${p.code}-${p.contractId ?? ""}-${p.day ?? i}`}>
                          {p.contractId && p.day ? (
                            <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openPendency(p)}>
                              Abrir espelho
                            </button>
                          ) : null}{" "}
                          {p.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </section>

              <section className="tkOfficial__section">
                <h3 className="tkOfficial__title">Integridade das marcações</h3>
                <button type="button" className="dpBtn dpBtn--secondary" onClick={verify} disabled={busy !== null}>
                  {busy === "verify" ? "Conferindo..." : "Conferir agora"}
                </button>
                {chain ? (
                  chain.ok ? (
                    <p className="dpNotice" style={{ marginTop: 8 }}>
                      Tudo íntegro: {chain.official.reduce((a, o) => a + o.records, 0)} registro(s) oficiais e {chain.internal.checkedPunches} marcação(ões)
                      conferidos.
                    </p>
                  ) : (
                    <div className="dpNotice dpNotice--warning" style={{ marginTop: 8 }}>
                      <strong>Divergência encontrada:</strong>
                      <ul className="dpRuleList" style={{ margin: "6px 0 0" }}>
                        {chain.official
                          .filter((o) => o.missingNsr !== null || o.brokenNsr !== null)
                          .map((o) => (
                            <li key={o.employerId}>
                              {o.legalName}: {o.missingNsr !== null ? `falta o NSR ${o.missingNsr}` : ""}
                              {o.missingNsr !== null && o.brokenNsr !== null ? "; " : ""}
                              {o.brokenNsr !== null ? `o código da marcação NSR ${o.brokenNsr} não confere` : ""}
                            </li>
                          ))}
                        {chain.internal.broken.map((b) => (
                          <li key={b.employeeId}>
                            {b.name}: a marcação NSR {b.nsr} não confere com a cadeia do colaborador
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                ) : (
                  <p className="dpMuted" style={{ margin: "8px 0 0" }}>
                    A conferência também roda todo dia, de madrugada; se algo não bater, ADMIN e DP recebem um aviso.
                  </p>
                )}
              </section>

              <p className="dpMuted" style={{ margin: 0 }}>
                O programa ainda não tem registro no INPI nem assinatura ICP-Brasil; os arquivos seguem o leiaute da Portaria 671 ({brDate(from)} a{" "}
                {brDate(to)}).
              </p>
            </>
          )}
        </div>

        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose}>
            Fechar
          </button>
        </footer>
      </div>

      <EcoConfirmDialog
        open={confirmStart}
        title="Iniciar o registro de ponto"
        message={
          <>
            Grava a identificação da empresa e inclui no registro todos os colaboradores com contrato vigente. Cada operação recebe um número
            (NSR) que não volta atrás. Seguir?
          </>
        }
        confirmLabel="Iniciar"
        busy={busy === "start"}
        onConfirm={start}
        onCancel={() => setConfirmStart(false)}
      />
    </div>
  );
}
